import { ConfigContext } from './context';
import { useState, type ReactNode, useMemo } from 'react';
import { Loader, Error, useLogout, useAuth } from '../index';
import { Button } from 'antd';
import styled from 'styled-components';

import useFetchSidebars from './hooks/useFetchSidebars';
import useFetchRoutes from './hooks/useFetchRoutes';

interface IProps {
  children: ReactNode | ReactNode[];
}

const ConfigProvider: React.FC<IProps> = ({ children }) => {
  const { user } = useAuth();
  const { logout } = useLogout();

  const { sidebar, isLoadingSidebar, errorFetchingSidebar } = useFetchSidebars({
    enabled: !!user,
  });

  const { routes, isLoadingRoutes, errorFetchingRoutes } = useFetchRoutes({
    enabled: !!user,
  });

  const sidebarItems = useMemo(() => {
    if (sidebar?.length) {
      return sidebar[0].items;
    }

    return [];
  }, [sidebar]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (isLoadingSidebar || isLoadingRoutes) {
    return <Loader />;
  }

  if (errorFetchingSidebar || errorFetchingRoutes) {
    return (
      <StyledError>
        <Error
          error={errorFetchingSidebar?.message || errorFetchingRoutes?.message}
        />
        <Button
          onClick={() => {
            logout();
            window.location.href = '/auth';
          }}
        >
          Logout
        </Button>
      </StyledError>
    );
  }

  return (
    <ConfigContext.Provider
      value={{
        routes: routes ?? [],
        sidebar: sidebarItems,
        sidebarCollapsed,
        onSidebarCollapse: setSidebarCollapsed,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

const StyledError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

export default ConfigProvider;
